import { ExtensionKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { call } from 'flow/actions/phone-actions';
import {
    selectOwnActiveProfileDescription,
    selectContactFixedNumber,
    selectContactMobileNumber,
} from 'flow/selectors/ExtensionSelectors';
import { selectOwnActiveLineData } from 'flow/selectors/call-selectors';
import {
    CallMethodType,
    convertE164NumberToTelavoxRoutingNumber,
} from 'flow/utils/call-utils';
import { selectIsEligibleForCoListeningAutoAlpha } from 'flow/utils/CentralSwitchUtils';
import {
    addPhoneLogEntry,
    COLISTENING_WIDGET_USERS,
} from 'flow/utils/browser-utils';

const PREFIX_CALL_CODE_COLISTENING = '*28*';
let coListenTimeoutId: ReturnType<typeof setTimeout> | null = null;

const selectExtensionKeyFromTopMsgUniqueId = (state: State, uniqueId: string) =>
    state.top.getIn(['uniqueIdToEntityKey', uniqueId]);

let extensionsToCoListenTo: ExtensionKey[] = [];

export const loadExtensionsToCoListenTo = () => {
    try {
        const storedUsers = localStorage.getItem(COLISTENING_WIDGET_USERS);
        if (!storedUsers) {
            extensionsToCoListenTo = [];
            return extensionsToCoListenTo;
        }
        extensionsToCoListenTo = JSON.parse(storedUsers) as ExtensionKey[];
        return extensionsToCoListenTo;
    } catch {
        extensionsToCoListenTo = [];
        return extensionsToCoListenTo;
    }
};

loadExtensionsToCoListenTo();

export const removeExtensionToCoListenTo = (extensionKey: ExtensionKey) => {
    try {
        const storedUsers = localStorage.getItem(COLISTENING_WIDGET_USERS);
        if (!storedUsers) return [];
        const currentKeys = JSON.parse(storedUsers) as ExtensionKey[];
        const updatedKeys = currentKeys.filter((key) => key !== extensionKey);
        localStorage.setItem(
            COLISTENING_WIDGET_USERS,
            JSON.stringify(updatedKeys),
        );
        extensionsToCoListenTo = updatedKeys;
        return extensionsToCoListenTo;
    } catch {
        // Handle error silently
        return [];
    }
};

export const handleAutoCoListenAlphaFeatureOnCallEstablished =
    (uniqueId: string) => (dispatch: any, getState: () => State) => {
        if (!selectIsEligibleForCoListeningAutoAlpha(getState())) return;

        if (
            selectOwnActiveProfileDescription(getState()) !==
            'Auto co-listening'
        )
            return;

        const ownActiveCalls = selectOwnActiveLineData(getState());
        if (ownActiveCalls && !ownActiveCalls.isEmpty()) return; // Logged-in user is in call, do not attempt auto co-listen

        const keyOfAnsweringExtension = selectExtensionKeyFromTopMsgUniqueId(
            getState(),
            uniqueId,
        );

        if (!extensionsToCoListenTo || extensionsToCoListenTo.length === 0) {
            addPhoneLogEntry(
                'Auto colistening profile active but no users added to auto co-listen to',
            );
            return;
        }

        if (
            !keyOfAnsweringExtension ||
            !extensionsToCoListenTo?.includes(keyOfAnsweringExtension)
        ) {
            return;
        }

        const phoneNumber =
            selectContactFixedNumber(getState(), keyOfAnsweringExtension) ||
            selectContactMobileNumber(getState(), keyOfAnsweringExtension);

        if (!phoneNumber) {
            addPhoneLogEntry(
                'Auto colistening failed: No phone number found for extension to co-listen to',
            );
            return;
        }

        const numberFormattedForCoListening = `${PREFIX_CALL_CODE_COLISTENING}${convertE164NumberToTelavoxRoutingNumber(phoneNumber)}`;

        if (coListenTimeoutId) clearTimeout(coListenTimeoutId);
        coListenTimeoutId = setTimeout(() => {
            coListenTimeoutId = null;
            addPhoneLogEntry('Attempting to auto co-listen');
            dispatch(
                call(numberFormattedForCoListening, CallMethodType.LOCAL, null),
            );
        }, 100);
    };
